/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';
import React from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography, {
  TypographyStyle,
} from '@/components/atoms/typography/typography';
import variables from '@/components/modules/information-carousel/information-carousel.variables';
import CarouselNavigation from '@/components/molecules/carousel-navigation/carousel-navigation';
import Splash from '@/components/molecules/splash/splash';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import TagList from '@/components/organisms/tag-list/tag-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import formatPrice from '@/libraries/formatters/format-price';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './information-carousel.module.scss';

export interface InformationCarouselProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline: Content.Components['InformationCarouselModuleDto']['headline'];
  /** The list of items to show */
  items: Content.Components['InformationCarouselModuleDto']['items'];
  /** The text to show as a pre headline */
  preHeadline: Content.Components['InformationCarouselModuleDto']['preHeadline'];
}

const InformationCarousel = ({
  headline,
  items = [],
  preHeadline,
  settings,
}: InformationCarouselProps) => {
  const isLaptop = useWindowSize('laptop');
  const [currentIndex, setCurrentIndex] = useState(0);
  const swiperRef = useRef<SwiperInstance>();
  const locale = getLocale();

  const renderContent = (
    item: Content.Components['InformationCarouselModuleDto']['items'][0],
  ) => (
    <AnimatePresence initial={false} mode="wait">
      <motion.div
        key={JSON.stringify(item)}
        animate={{ opacity: 1 }}
        className={styles.contentWrapper}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'linear' }}
      >
        <div>
          <div className={styles.headlineWrapper}>
            {item.preHeadline && (
              <Typography className={styles.textCaption} tagStyle="labelSmall">
                {item.preHeadline}
              </Typography>
            )}

            {item.headline && (
              <Typography
                className={styles.headline}
                tagStyle={variables.headlineTagStyle as TypographyStyle}
              >
                {item.headline}
              </Typography>
            )}

            {item.subHeadline && (
              <Typography
                className={styles.headerCaption}
                tagStyle="priceMedium"
              >
                {item.subHeadline}
              </Typography>
            )}
          </div>

          {item.tags && item.tags.length > 0 && (
            <TagList
              className={styles.tagList}
              tags={item.tags.map(tag => ({
                text: tag,
                variant: 'secondary',
              }))}
            />
          )}

          {item.text && (
            <RichText className={styles.description} text={item.text} />
          )}
        </div>

        {item.facts && item.facts.length > 0 && (
          <div className={styles.contentItems}>
            {item.facts.map((fact, factIndex) => (
              <div key={factIndex} className={styles.contentItem}>
                <Typography
                  className={styles.textCaption}
                  tagStyle="bodySmallBold"
                >
                  {fact.title}
                </Typography>

                <Typography
                  className={styles.headline}
                  tagStyle="bodyLargeBold"
                >
                  {fact.formattedValue
                    ? fact.formattedValue
                    : fact.value
                      ? `${formatPrice(
                          { amount: fact.value.value },
                          { locale: getCulture(locale) },
                        )} ${fact.value.unitOfMeasure}`
                      : ''}
                </Typography>
              </div>
            ))}
          </div>
        )}

        {item.link && (
          <CtaLinkList
            items={[
              {
                $type: 'ButtonLinkCtaDto',
                link: item.link,
                settings: {
                  variant: 'primary',
                },
              },
            ]}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <ModuleWrapper className={classNames(styles.component)} settings={settings}>
      <Container className={styles.container}>
        <Row className={styles.row}>
          <Column
            className={styles.textColumn}
            offset={{ desktop: 3, laptop: 2 }}
            width={{ desktop: 6, laptop: 8 }}
          >
            {preHeadline && (
              <Typography
                className={styles.preHeadline}
                tag="p"
                tagStyle="subHeading"
              >
                {preHeadline}
              </Typography>
            )}

            {headline && (
              <Typography
                className={styles.headline}
                tag="h2"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>
            )}
          </Column>
        </Row>

        <Row>
          <Column
            className={styles.itemsColumn}
            offset={{ desktop: 4, laptop: 1 }}
            width={{ desktop: 4, laptop: 5, mobile: 11, tablet: 8 }}
          >
            <Swiper
              key={`swiper-${isLaptop}`}
              breakpoints={{
                [parseInt(breakpoints.mobile)]: {
                  spaceBetween: -32,
                },

                [parseInt(breakpoints.tablet)]: {
                  spaceBetween: 0,
                },

                [parseInt(breakpoints.laptop)]: {
                  spaceBetween: 32,
                },
              }}
              className={styles.swiper}
              dir={isLaptop ? 'rtl' : 'ltr'}
              onSlideChangeTransitionEnd={item => {
                setCurrentIndex(item.snapIndex);
              }}
              onSlideChangeTransitionStart={item => {
                setCurrentIndex(item.snapIndex);
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              speed={500}
            >
              {items.map((item, itemIndex) => (
                <SwiperSlide
                  key={itemIndex}
                  className={classNames(styles.item, {
                    [styles.isActive]: itemIndex === currentIndex,
                  })}
                >
                  {item.image && (
                    <React.Fragment>
                      {item.link ? (
                        <Link className={styles.itemImage} url={item.link.url}>
                          <Image
                            alt={item.image.altText}
                            aspectRatio="threeByFour"
                            className={styles.itemImage}
                            focalPoint={item.image.focalPoint}
                            height={item.image.dimensions?.height ?? 0}
                            src={item.image.url}
                            width={item.image.dimensions?.width ?? 0}
                          />
                        </Link>
                      ) : (
                        <Image
                          alt={item.image.altText}
                          aspectRatio="threeByFour"
                          className={styles.itemImage}
                          focalPoint={item.image.focalPoint}
                          height={item.image.dimensions?.height ?? 0}
                          src={item.image.url}
                          width={item.image.dimensions?.width ?? 0}
                        />
                      )}
                    </React.Fragment>
                  )}

                  {item.splash && (
                    <Splash className={styles.itemSplash} {...item.splash} />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>

            <CarouselNavigation
              className={styles.carouselNavigation}
              showPageCount
              swiperRef={swiperRef}
            />
          </Column>

          <Column
            className={styles.contentColumn}
            width={{ desktop: 3, laptop: 5 }}
          >
            {renderContent(items[currentIndex])}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

InformationCarousel.displayName = 'InformationCarousel';

export default InformationCarousel;
